import Info from "@mui/icons-material/Info";
import WarningOutlined from "@mui/icons-material/WarningOutlined";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { LinearIcon } from "components/LinearIcon";
import { getActivityTitleOnly } from "encamp-shared/src/activity/activityDisplayNames";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  ActivityStatus,
  ActivityType,
  JobStatus,
} from "generated-graphql/graphql";
import { useActivityLogs } from "hooks/useActivityLogs";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useReportDetails } from "../useReportDetails";
import { ActivityBottomToolbar } from "./ActivityBottomToolbar";
import { ActivityErrorDisplay } from "./ActivityErrorDisplay";
import { ActivityInputs } from "./ActivityInputs";
import { CreateLinearIssueDialog } from "./CreateLinearIssueDialog";
import { ReportDetailsExtended } from "./ReportDetailsExtended";
import { StaffInputs } from "./StaffInputs";
import { SupplementalAgencyCommDetails } from "./SupplementalAgencyCommDetails";

function getActivityStatusChipColor(
  status: ActivityStatus
):
  | "default"
  | "primary"
  | "secondary"
  | "error"
  | "info"
  | "success"
  | "warning" {
  switch (status) {
    case ActivityStatus.InProgress:
      return "primary"; // blue
    case ActivityStatus.Error:
      return "error"; // red
    case ActivityStatus.Completed:
      return "success"; // green
    case ActivityStatus.StaffCompleted:
      return "success"; // green (similar to completed)
    case ActivityStatus.NotStarted:
      return "default"; // gray
    case ActivityStatus.Queued:
      return "info"; // light blue (queued/waiting)
    default:
      return "default"; // gray fallback
  }
}

function getJobStatusLinkColor(
  jobStatus: JobStatus | undefined | null
): string {
  switch (jobStatus) {
    // Gray - not started
    case JobStatus.Queued:
      return "text.secondary"; // gray

    // Blue - in progress/running
    case JobStatus.Running:
    case JobStatus.Dispatched:
    case JobStatus.Provisioning:
    case JobStatus.ProvisioningEcsTask:
    case JobStatus.Cancelling:
      return "primary.main"; // blue

    // Red - failed
    case JobStatus.Failed:
    case JobStatus.FailedMayAutoRetry:
    case JobStatus.FailedNeedsHuman:
    case JobStatus.FailedProvisioningEcsTask:
    case JobStatus.FailedQualityControlCheck:
      return "error.main"; // red

    // Green - success
    case JobStatus.Succeeded:
      return "success.main"; // green

    // Orange/Warning - awaiting intervention, cancelled, etc.
    case JobStatus.AwaitingManualIntervention:
    case JobStatus.Cancelled:
    case JobStatus.ShouldCancel:
    case JobStatus.Promoted:
      return "warning.main"; // orange

    default:
      return "text.secondary"; // gray fallback
  }
}

export function ActivityDetails() {
  const theme = useTheme();
  const { data, loading } = useReportDetails();
  const { activityId, reportId } = useParams<{
    activityId: string;
    reportId: string;
  }>();
  const [linearDialogOpen, setLinearDialogOpen] = useState(false);

  const activity = useMemo(
    () =>
      data?.tierIIReport.currentWorkflow?.activities.find(
        (act) => act.id === activityId
      ),
    [activityId, data?.tierIIReport.currentWorkflow?.activities]
  );

  const { data: logData, loading: loadingActivityLog } = useActivityLogs(
    activity?.id,
    `status:${ActivityStatus.Error}`,
    0,
    1,
    [{ field: "time", sort: "desc" }]
  );

  const recentErrorMessage =
    logData?.activityLogs.items?.[0]?.message ?? "Unknown Error";

  const handleOpenLinearDialog = useCallback(() => {
    setLinearDialogOpen(true);
  }, []);

  const handleCloseLinearDialog = useCallback(() => {
    setLinearDialogOpen(false);
  }, []);

  // Extract data needed for the Linear dialog
  const workflowId = data?.tierIIReport.currentWorkflow?.id;
  const facility = data?.tierIIReport.facility;
  const tenant = data?.tierIIReport.tenant;
  const isActivityInError = activity?.status === ActivityStatus.Error;

  return (
    <Stack
      flexDirection="column"
      justifyContent="space-between"
      sx={{ flex: 1, maxHeight: 700, overflowY: 'auto' }}
      padding={theme.spacing(2)}
    >
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h6"
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
        >
          {activity && getActivityTitleOnly(activity)}
          {activity && (
            <Chip
              label={prettyPrintEnumValue(activity.status)}
              color={getActivityStatusChipColor(activity.status)}
              size="small"
              variant="filled"
            />
          )}
          {/* Linear issue button - show when activity is in error */}
          {isActivityInError && (
            <Tooltip title="Create Linear Issue">
              <IconButton
                size="small"
                onClick={handleOpenLinearDialog}
                sx={{ ml: "auto" }}
                aria-label="Create Linear Issue"
              >
                <LinearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {activity && activity.type === ActivityType.Staff && !isActivityInError && (
            <Tooltip title="Staff Submission Workflow">
              <Link
                sx={{ display: "flex", ml: "auto" }}
                component="a"
                href="https://academy.encamp.com/chemicals/6NmwbK2GNFS9Az6DhF7Xez"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Info />
              </Link>
            </Tooltip>
          )}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          {activity?.description}
        </Typography>

        {activity && activity.currentJob &&
          activity.type !== ActivityType.SendSupplementalAgencyCommunication && (
            <Link
              href={`/staff/jobs/${activity?.currentJob?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                color: getJobStatusLinkColor(activity.currentJob.status),
                textDecoration: "none",
                fontSize: "0.875rem",
                fontWeight: 400,
                my: 2,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
            >
              Job Status:{" "}
              {prettyPrintEnumValue(activity?.currentJob?.status) ?? "Unknown"}
            </Link>
          )}
      </Box>

      {activity && activity.status === ActivityStatus.Error && (
        <Box sx={{ flex: 1 }} gap={1} mb={2}>
          {loadingActivityLog ? (
            <CircularProgress />
          ) : (
            <Stack direction="row" gap={1}>
              <WarningOutlined sx={{ color: "red" }} />
              <Typography variant="subtitle1" sx={{ mb: 2, color: "red" }}>
                {recentErrorMessage}
              </Typography>
            </Stack>
          )}
          <ActivityErrorDisplay activity={activity} />
        </Box>
      )}

      <Box sx={{ flex: 1 }} gap={1}>
        {loading ? (
          <>
            <Skeleton variant="rounded" sx={{ height: "3rem", mb: 2 }} />
            <Skeleton variant="rounded" sx={{ height: "3rem", mb: 2 }} />
            <Skeleton variant="rounded" sx={{ height: "3rem", mb: 2 }} />
            <Skeleton variant="rounded" sx={{ height: "3rem", mb: 2 }} />
          </>
        ) : activity ? (
          <>
            {activity.status === ActivityStatus.StaffCompleted && (
              <Typography variant="subtitle1" fontStyle="italic" sx={{ mb: 1 }}>
                Waiting for the backend workflow manager to clean up/complete
                this activity
              </Typography>
            )}
            <StaffInputs />
            <ActivityInputs activity={activity} />
            <ReportDetailsExtended
              reportDetailsExtended={activity.reportDetailsExtended}
            />
            {activity.type ===
              ActivityType.SendSupplementalAgencyCommunication && (
              <SupplementalAgencyCommDetails activityId={activity.id} />
            )}
          </>
        ) : (
          <> {"Uh Oh can't find this activity"}</>
        )}
      </Box>
      {activity && <ActivityBottomToolbar activity={activity} />}

      {/* Linear Issue Dialog */}
      {isActivityInError &&
        workflowId &&
        reportId &&
        facility &&
        tenant &&
        activity && (
          <CreateLinearIssueDialog
            open={linearDialogOpen}
            onClose={handleCloseLinearDialog}
            workflowId={workflowId}
            reportId={reportId}
            facility={{
              id: facility.id,
              name: facility.name,
              city: facility.city ?? "",
              state: facility.state ?? "",
              streetAddress1: facility.streetAddress1,
            }}
            tenant={{
              id: tenant.id,
              name: tenant.name,
            }}
            activity={{
              id: activity.id,
              type: activity.type,
              description: activity.description,
            }}
            errorMessage={recentErrorMessage}
          />
        )}
    </Stack>
  );
}
